import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Todo } from '@almanac/contracts';
import { useApi } from '../../api/useApi';
import { useAssistant } from '../assistant/AssistantProvider';
import { askAlmanacAssistant } from '../../lib/ask-assistant';
import { useGlobalInputs, type InputComposerKind } from './InputsContext';

const InputComposer = lazy(async () => {
  const module = await import('./InputComposer');
  return { default: module.InputComposer };
});

function openTodos(todos: Todo[]): Todo[] {
  return todos.filter(
    (todo) => todo.archived_at === null && todo.status !== 'done' && todo.status !== 'canceled',
  );
}

function dueLabel(todo: Todo): string | null {
  if (todo.snoozed_until && new Date(todo.snoozed_until) > new Date()) {
    return `Snoozed until ${new Date(todo.snoozed_until).toLocaleDateString()}`;
  }
  if (!todo.due_at) return null;
  const date = new Date(todo.due_at);
  const overdue = date < new Date();
  return `${overdue ? 'Overdue · ' : ''}${date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

export function GlobalInputsLayout({ children }: { children: ReactNode }) {
  const { railOpen, railPinned } = useGlobalInputs();
  return (
    <>
      <div className={railOpen && railPinned ? 'min-w-0 lg:pr-88' : 'min-w-0'}>{children}</div>
      <DesktopTodoRail />
      <MobileTodoDrawer />
      <GlobalCreateButton />
      <Suspense fallback={null}>
        <InputComposer />
      </Suspense>
    </>
  );
}

function DesktopTodoRail() {
  const { railOpen } = useGlobalInputs();
  if (!railOpen) return null;
  return (
    <aside
      aria-label="Global to-dos"
      className="fixed top-16 right-0 bottom-0 z-30 hidden w-88 overflow-y-auto bg-card shadow-xl ring-1 ring-foreground/5 lg:block"
    >
      <TodoRailContent />
    </aside>
  );
}

function MobileTodoDrawer() {
  const { railOpen, setRailOpen } = useGlobalInputs();

  useEffect(() => {
    if (!railOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setRailOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [railOpen, setRailOpen]);

  if (!railOpen) return null;
  return (
    <dialog
      open
      className="fixed inset-0 z-40 m-0 size-full max-h-none max-w-none bg-transparent p-0 lg:hidden"
      aria-label="To-dos"
    >
      <button
        type="button"
        aria-label="Close to-dos"
        onClick={() => setRailOpen(false)}
        className="absolute inset-0 size-full bg-foreground/35"
      />
      <section className="absolute right-0 bottom-0 left-0 max-h-[88dvh] overflow-y-auto rounded-t-xl bg-card pb-[env(safe-area-inset-bottom)] shadow-2xl ring-1 ring-foreground/10">
        <div
          className="mx-auto my-2 size-10 h-1 rounded-full bg-foreground/15"
          aria-hidden="true"
        />
        <TodoRailContent mobile />
      </section>
    </dialog>
  );
}

function TodoRailContent({ mobile = false }: { mobile?: boolean }) {
  const api = useApi();
  const { snapshot, error, railPinned, setRailPinned, setRailOpen, openComposer, refresh } =
    useGlobalInputs();
  const todos = useMemo(() => openTodos(snapshot?.todos ?? []), [snapshot]);
  const proposals = (snapshot?.proposals ?? []).filter((proposal) => proposal.status === 'pending');
  const dueCount = todos.filter((todo) => {
    const due = todo.remind_at ?? todo.due_at;
    return (
      due !== null &&
      new Date(due) <= new Date() &&
      (!todo.snoozed_until || new Date(todo.snoozed_until) <= new Date())
    );
  }).length;
  const [workingId, setWorkingId] = useState<string | null>(null);

  async function mutate(id: string, action: () => Promise<unknown>) {
    setWorkingId(id);
    try {
      await action();
      await refresh();
    } finally {
      setWorkingId(null);
    }
  }

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">To-dos</h2>
          <p className="text-base text-muted-foreground sm:text-sm">
            {todos.length} open{dueCount > 0 ? ` · ${dueCount} due` : ''}.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!mobile ? (
            <button
              type="button"
              aria-pressed={railPinned}
              onClick={() => setRailPinned(!railPinned)}
              className="rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {railPinned ? 'Unpin' : 'Pin'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (railPinned) setRailPinned(false);
              setRailOpen(false);
            }}
            className="relative rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            Close
            <span
              className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {error ? <p className="pt-3 text-base text-danger sm:text-sm">{error}</p> : null}
      {!snapshot && !error ? (
        <div className="space-y-3 pt-4" aria-hidden="true">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-16 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : null}
      {snapshot && todos.length === 0 ? (
        <div className="space-y-3 py-8 text-center">
          <p className="text-base font-medium text-foreground sm:text-sm">Nothing open.</p>
          <p className="text-base text-muted-foreground sm:text-sm">
            Create a global to-do when something should survive beyond one digest.
          </p>
          <button
            type="button"
            onClick={() => openComposer('todo')}
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground ring-1 ring-primary"
          >
            New to-do
          </button>
        </div>
      ) : null}

      <ul className="divide-y divide-foreground/10 pt-3">
        {todos.slice(0, 12).map((todo) => {
          const label = dueLabel(todo);
          return (
            <li key={todo.todo_id} className="py-3">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  aria-label={`Complete ${todo.title}`}
                  disabled={workingId === todo.todo_id}
                  onClick={() =>
                    void mutate(todo.todo_id, () =>
                      api.updateTodo(todo.todo_id, { status: 'done' }),
                    )
                  }
                  className="relative mt-0.5 size-5 shrink-0 rounded-full border border-foreground/25 hover:border-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:size-4"
                >
                  <span
                    className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => openComposer('todo', todo)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-base font-medium text-foreground sm:text-sm">
                    {todo.title}
                  </p>
                  <p
                    className={
                      label?.startsWith('Overdue')
                        ? 'text-base text-danger sm:text-sm'
                        : 'text-base text-muted-foreground sm:text-sm'
                    }
                  >
                    {label ?? todo.priority}
                  </p>
                </button>
                <button
                  type="button"
                  aria-label={`Snooze ${todo.title} for one day`}
                  onClick={() => {
                    const until = new Date();
                    until.setDate(until.getDate() + 1);
                    void mutate(todo.todo_id, () =>
                      api.updateTodo(todo.todo_id, { snoozed_until: until.toISOString() }),
                    );
                  }}
                  className="relative shrink-0 rounded-md px-1.5 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  Snooze
                  <span
                    className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {proposals.length > 0 ? (
        <section className="border-t border-foreground/10 pt-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-foreground sm:text-sm">
                Proposed by Almanac
              </h3>
              <p className="text-base text-muted-foreground sm:text-sm">
                Separate until you accept.
              </p>
            </div>
            <span className="rounded-full bg-accent/20 px-2 py-1 text-sm text-accent-foreground">
              {proposals.length}
            </span>
          </div>
          <ul className="divide-y divide-foreground/10 pt-2">
            {proposals.slice(0, 3).map((proposal) => (
              <li key={proposal.proposal_id} className="space-y-2 py-3">
                <p className="text-base font-medium text-foreground sm:text-sm">{proposal.title}</p>
                {proposal.rationale_md ? (
                  <p className="line-clamp-2 text-base text-muted-foreground sm:text-sm">
                    {proposal.rationale_md}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={workingId === proposal.proposal_id}
                    onClick={() =>
                      void mutate(proposal.proposal_id, () =>
                        api.acceptTodoProposal(proposal.proposal_id),
                      )
                    }
                    className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
                  >
                    Accept
                  </button>
                  <Link
                    to="/inputs?tab=proposals"
                    onClick={() => setRailOpen(false)}
                    className="rounded-md bg-muted px-3 py-1.5 text-sm text-foreground"
                  >
                    Edit and accept
                  </Link>
                  <button
                    type="button"
                    disabled={workingId === proposal.proposal_id}
                    onClick={() =>
                      void mutate(proposal.proposal_id, () =>
                        api.rejectTodoProposal(proposal.proposal_id),
                      )
                    }
                    className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="flex items-center justify-between gap-3 border-t border-foreground/10 pt-4">
        <Link
          to="/inputs"
          onClick={() => mobile && setRailOpen(false)}
          className="rounded-md px-2 py-1.5 text-sm text-primary hover:bg-primary/10"
        >
          Manage all inputs
        </Link>
        <button
          type="button"
          onClick={() => openComposer('todo')}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground ring-1 ring-primary"
        >
          New to-do
        </button>
      </div>
    </div>
  );
}

function GlobalCreateButton() {
  const { railOpen, railPinned, openComposer } = useGlobalInputs();
  const assistant = useAssistant();
  const [open, setOpen] = useState(false);
  const createOptions: Array<{ kind: InputComposerKind; label: string; detail: string }> = [
    { kind: 'todo', label: 'To-do', detail: 'A global action item' },
    { kind: 'research', label: 'Research', detail: 'Queue a normal research pass' },
    { kind: 'guidance', label: 'Guidance', detail: 'Standing preferences for agents' },
  ];

  function choose(kind: InputComposerKind) {
    setOpen(false);
    openComposer(kind);
  }

  function openAssistant() {
    setOpen(false);
    askAlmanacAssistant({});
  }

  // The dock hides the pill (its grounding chip covers the same intent); on
  // lg+ the whole stack shifts left of the open dock so the FAB stays usable
  // while chatting. Otherwise the pinned rail dictates the offset.
  const dockOpen = assistant.open;
  const showAskPill = assistant.groundingResearchId !== null && !dockOpen;
  let containerOffset =
    'fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-30 flex flex-col items-end gap-2 sm:right-6';
  if (dockOpen) {
    containerOffset =
      'fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-30 flex flex-col items-end gap-2 max-lg:hidden lg:right-[27rem]';
  } else if (railOpen && railPinned) {
    containerOffset =
      'fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-30 flex flex-col items-end gap-2 lg:right-92';
  }

  return (
    <div className={containerOffset}>
      {showAskPill ? (
        <button
          type="button"
          onClick={openAssistant}
          className="rounded-full bg-card px-3 py-2 text-sm font-medium text-primary shadow-lg ring-1 ring-primary/25"
        >
          Ask about this research
        </button>
      ) : null}
      {open ? (
        <div className="flex flex-col items-end gap-2" role="menu" aria-label="Create input">
          <button
            type="button"
            role="menuitem"
            onClick={openAssistant}
            className="rounded-xl bg-card px-3 py-2 text-left shadow-lg ring-1 ring-foreground/10"
          >
            <span className="text-sm font-medium text-foreground">Ask</span>
            <span className="ml-2 text-sm text-muted-foreground max-sm:hidden">
              Chat with Almanac
            </span>
          </button>
          {createOptions.map((option) => (
            <button
              key={option.kind}
              type="button"
              role="menuitem"
              onClick={() => choose(option.kind)}
              className="rounded-xl bg-card px-3 py-2 text-left shadow-lg ring-1 ring-foreground/10"
            >
              <span className="text-sm font-medium text-foreground">{option.label}</span>
              <span className="ml-2 text-sm text-muted-foreground max-sm:hidden">
                {option.detail}
              </span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <TodoRailToggle />
        <button
          type="button"
          aria-label={open ? 'Close create menu' : 'Create an Almanac input'}
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className="grid size-12 shrink-0 place-items-center rounded-full bg-primary text-xl text-primary-foreground shadow-lg ring-1 ring-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {open ? '×' : '+'}
        </button>
      </div>
    </div>
  );
}

function TodoRailToggle() {
  const { snapshot, railOpen, setRailOpen } = useGlobalInputs();
  const count = openTodos(snapshot?.todos ?? []).length;
  const proposals = (snapshot?.proposals ?? []).filter(
    (proposal) => proposal.status === 'pending',
  ).length;
  return (
    <button
      type="button"
      aria-expanded={railOpen}
      onClick={() => setRailOpen(!railOpen)}
      className="rounded-full bg-card px-3 py-2 text-sm font-medium text-foreground shadow-lg ring-1 ring-foreground/10"
    >
      {count} to-do{count === 1 ? '' : 's'}
      {proposals > 0 ? ` · ${proposals} proposed` : ''}
    </button>
  );
}
